import { memo } from 'react';
import { Outlet, useLocation } from 'react-router-dom';

import { appColors, BackrsSidebar, SIDEBAR_COLLAPSED_WIDTH } from '@backrs_ui/common-ui';

import { APP_ROUTES, LegacyPageTypes } from '@backrs_ui/grow-ui/src/App';
import { setCurrentDialogNugget } from '@backrs_ui/grow-ui/src/redux/contentNugget/contentNugget';
import { useAppDispatch, useAppSelector } from '@backrs_ui/grow-ui/src/redux/hooks';

import { Box, Grid, SxProps, Theme, useMediaQuery } from '@mui/material';
import { usePostHog } from 'posthog-js/react';

import { usePreLinkedParams } from './pageShell.hooks';
import { useMenuSections } from './sections/menuSections.hook';
import { MappedDialogNugget } from '../Nuggets/MappedDialogNugget';

const MemoizedBackrsSidebar = memo(BackrsSidebar);

export const PageShell = memo(() => {
  const isMobile = useMediaQuery((theme: Theme) => theme.breakpoints.down('md'));

  const dispatch = useAppDispatch();
  const location = useLocation();
  const postHog = usePostHog();

  const sidebar = useMenuSections();

  usePreLinkedParams(dispatch, postHog);

  const { currentDialogNugget } = useAppSelector((state) => state.contentNugget);

  const currentRoute = Object.values(APP_ROUTES)
    .sort((a, b) => b.path.length - a.path.length)
    .find((route) => location.pathname.startsWith(route.path.replace('/:id?', '')));

  const legacyPageType: LegacyPageTypes | undefined = currentRoute?.legacyPageType ?? undefined;
  const childrenContainerSx = legacyPageType
    ? PAGE_TYPE_STYLE_MAP[legacyPageType]
    : commonPageBodyStyle;

  const backrs40GridLayout = (
    <Box sx={contentContainerStyle} id="backrs-layout">
      <Box sx={childrenContainerSx}>
        <Grid container columns={9} sx={gridSx}>
          <Outlet />
        </Grid>
      </Box>
    </Box>
  );

  const unauthenticatedAndLegacyLayout = (
    <Box sx={childrenContainerSx}>
      <Outlet />
    </Box>
  );

  return (
    <>
      <MappedDialogNugget
        activeDialogNugget={currentDialogNugget}
        handleClose={() => dispatch(setCurrentDialogNugget())}
      />
      <Box sx={pageShellStyle}>
        {sidebar && legacyPageType !== 'UNAUTHENTICATED' && (
          <Box
            sx={legacyPageType === 'BACKRS_4.0' && !isMobile ? sidebarStyleBackrs40 : sidebarStyle}
          >
            <MemoizedBackrsSidebar sidebar={sidebar} />
          </Box>
        )}

        {legacyPageType === 'BACKRS_4.0' ? backrs40GridLayout : unauthenticatedAndLegacyLayout}
      </Box>
    </>
  );
});

const pageShellStyle: SxProps<Theme> = {
  display: 'flex',
  height: '100svh',
};

const contentContainerStyle: SxProps<Theme> = {
  flexGrow: 1,
  width: '100%',
  height: '100svh',
  overflowY: 'auto',
  marginLeft: { xs: '0', md: `${0}px`, lg: 0 },
};

const sidebarStyle: SxProps<Theme> = {
  flexShrink: 0,
  overflow: 'hidden',
  position: 'fixed',
  zIndex: 100,
};

const sidebarStyleBackrs40: SxProps<Theme> = {
  // SMALL SCREEN PAGINATION CONTAINER
  '@media (max-height: 768px)': {
    ...sidebarStyle,
  },
};

const unauthenticatedPageBodyStyle: SxProps<Theme> = ({ backrs }) => ({
  width: '100%',
  backgroundColor: backrs.colors['Beige/50'],
});

const commonPageBodyStyle: SxProps<Theme> = {
  width: '100%',
};

const gridSx: SxProps<Theme> = {
  width: '100%',
  margin: '0 auto',
};

const blueprintsPageBodyStyle: SxProps<Theme> = {
  ...commonPageBodyStyle,
  backgroundImage: `linear-gradient(${appColors.color.blue1}33 1px, transparent 1px),
                    linear-gradient(90deg, ${appColors.color.blue1}33 1px, transparent 1px)`,
  backgroundSize: '28px 28px',
  backgroundColor: appColors.neutral.white,
};

const prosperPageBodyStyle: SxProps<Theme> = {
  ...commonPageBodyStyle,
  backgroundColor: appColors.color.taupe2,
  marginLeft: `${SIDEBAR_COLLAPSED_WIDTH}px`,
};

const lubavPageBodyStyle: SxProps<Theme> = {
  ...commonPageBodyStyle,
  WebkitFontSmoothing: 'antialiased',
  MozOsxFontSmoothing: 'grayscale',
  backgroundColor: appColors.neutral.white,
  backgroundImage: `linear-gradient(${appColors.color.blue1}33 1px, transparent 1px)`,
  backgroundSize: '24px 32px',
  backgroundPosition: '-8px -8px',
  marginLeft: `${SIDEBAR_COLLAPSED_WIDTH}px`,
};

const backrs40PageBodyStyle: SxProps<Theme> = ({ backrs }) => ({
  backgroundColor: backrs.colors['Beige/25'],
  minHeight: '100svh',
  width: '100%',
  display: 'flex',
  paddingLeft: { xs: `${SIDEBAR_COLLAPSED_WIDTH}px`, md: 'unset' },
});

export const PAGE_TYPE_STYLE_MAP: Record<LegacyPageTypes, SxProps<Theme>> = {
  BLUEPRINT: blueprintsPageBodyStyle,
  PROSPER: prosperPageBodyStyle,
  LUBAV: lubavPageBodyStyle,
  UNAUTHENTICATED: unauthenticatedPageBodyStyle,
  'BACKRS_4.0': backrs40PageBodyStyle,
};
